import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import DeleteModal from '@atom/components/common/DeleteModal';
import { Draggable, Droppable } from '@atom/components/common/dragAndDrop';
import SchemaDetailContext, {
  DragDropType,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  ATTRIBUTE_CREATE,
  ATTRIBUTE_GROUP_DELETE,
  ATTRIBUTE_GROUP_UPDATE,
} from '@atom/graph/schema';
import { Icon, IconButton, List, Tooltip } from '@atom/mui';
import { ControlPointIcon, DeleteIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { DataType } from '@atom/types/dataType';
import {
  Schema,
  SchemaTreeAttribute,
  SchemaTreeAttributeGroup,
} from '@atom/types/schema';

import AddItemModal from '../AddItemModal';
import EditItemModal from '../EditItemModal';
import ListItemWrapper from '../subItemTree/ListItemWrapper';

import AttributeRow from './AttributeRow';
import {
  addPendingCreation,
  INITIAL_TEMP_ATTRIBUTE,
  mergePendingCreationsAndGroupChanges,
} from './subItemDetailUtilities';

import classes from './subItemDetail.module.css';

const styles = {
  list: {
    padding: 0,
    minHeight: '1rem',
  },
};

const DEFAULT_ATTRIBUTE = {
  dataType: DataType.SHORT_TEXT,
  isFilterable: false,
  isVisibleAsSubtext: false,
  isEditable: true,
  isRequired: false,
};

interface Props {
  attributeGroup: SchemaTreeAttributeGroup;
}

const AttributeGroupRow = ({ attributeGroup }: Props) => {
  const container = useRef<HTMLDivElement>(null);

  const {
    schemaTree,
    refetchSchemaTree,
    selectedSubItem,
    setSelectedAttributeRoute,
    setSelectedAttribute,
    selectedAttributeGroup,
    setSelectedAttributeGroup,
    pendingGroupUpdates,
    pendingCreations,
    setPendingCreations,
    setPendingSchemaUpdates,
  } = useContext(SchemaDetailContext);

  const [createdAttributeId, setCreatedAttributeId] = useState<string>();

  // If pending creations or group updates exist, they will be merged with the
  // attribute group and attributes data
  const mergedAttributeGroup = mergePendingCreationsAndGroupChanges(
    selectedSubItem.id,
    attributeGroup,
    pendingCreations,
    pendingGroupUpdates,
  );

  const [overflowing, setOverflowing] = useState<boolean>(false);
  const [openAttributeCreate, setOpenAttributeCreate] =
    useState<boolean>(false);
  const [openAttributeGroupEdit, setOpenAttributeGroupEdit] =
    useState<boolean>(false);
  const [openAttributeGroupDelete, setOpenAttributeGroupDelete] =
    useState<boolean>(false);

  const [createAttribute, { loading: creatingAttribute }] =
    useMutation(ATTRIBUTE_CREATE);

  const [updateAttributeGroup, { loading: updatingGroup }] = useMutation(
    ATTRIBUTE_GROUP_UPDATE,
  );

  const [deleteAttributeGroup, { loading: deletingGroup }] = useMutation(
    ATTRIBUTE_GROUP_DELETE,
  );

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [mergedAttributeGroup.name]);

  const getNewAttribute = (schema: Schema) => {
    const currentGroupIndex = R.findIndex(R.propEq(attributeGroup.id, 'id'))(
      schema.attributeGroups,
    );
    const newAttributeId = R.last(
      schema.attributeGroups[currentGroupIndex]?.attributes,
    );

    return { id: newAttributeId, ...schema.attributes[newAttributeId] };
  };

  const handleAttributeCreate = async (name: string) => {
    if (schemaTree?.isPublished) {
      // Reordering is not supported on published schemas with temp attributes
      // Clear any reordering that has been done on the parent sub item
      setPendingSchemaUpdates(prevUpdates => {
        return R.omit([selectedSubItem.id], prevUpdates);
      });

      const tempAttribute = {
        ...INITIAL_TEMP_ATTRIBUTE,
        name,
        attributeGroupId: mergedAttributeGroup.id,
        id: uuid(),
      };

      setPendingCreations(prev =>
        addPendingCreation(selectedSubItem.id, prev, tempAttribute),
      );

      setOpenAttributeCreate(false);

      setSelectedAttributeRoute({
        attributeGroupId: attributeGroup.id,
        attributeId: tempAttribute.id,
      });
      setSelectedAttributeGroup(null);
      setSelectedAttribute(tempAttribute);
      setCreatedAttributeId(tempAttribute.id);
    } else {
      const { data } = await createAttribute({
        variables: {
          input: {
            ...DEFAULT_ATTRIBUTE,
            schemaId: selectedSubItem?.id,
            attributeGroupId: mergedAttributeGroup?.id,
            name,
          },
        },
      });

      await refetchSchemaTree();

      const newAttribute = getNewAttribute(data?.attributeCreate);

      setSelectedAttributeRoute({
        attributeGroupId: attributeGroup.id,
        attributeId: newAttribute.id,
      });
      setSelectedAttributeGroup(null);
      setSelectedAttribute(newAttribute);
      setCreatedAttributeId(newAttribute.id);
    }

    setOpenAttributeCreate(false);
  };

  const handleAttributeGroupEdit = async (name: string) => {
    await updateAttributeGroup({
      variables: {
        input: {
          schemaId: selectedSubItem?.id,
          attributeGroupId: mergedAttributeGroup?.id,
          name,
        },
      },
    });

    await refetchSchemaTree();

    setOpenAttributeGroupEdit(false);
  };

  const handleAttributeGroupDelete = async () => {
    await deleteAttributeGroup({
      variables: {
        input: {
          schemaId: selectedSubItem?.id,
          attributeGroupId: mergedAttributeGroup?.id,
        },
      },
    });

    await refetchSchemaTree();

    setSelectedAttributeRoute(null);
    setSelectedAttribute(null);
    setSelectedAttributeGroup(null);
    setOpenAttributeGroupDelete(false);
  };

  const handleAttributeGroupClick = () => {
    setSelectedAttribute(null);
    setSelectedAttributeGroup(mergedAttributeGroup);
  };

  const isDragDropDisabled = R.has(selectedSubItem.id)(pendingCreations);

  const showDeleteAttribute =
    !schemaTree?.isPublished && selectedSubItem.attributeGroups.length > 1;

  const isSelected = selectedAttributeGroup?.id === mergedAttributeGroup.id;

  const textStyles = {
    color: isSelected ? colors.brand.blue : colors.neutral.dark,
  };

  return (
    <>
      <ListItemWrapper
        selected={isSelected}
        onClick={handleAttributeGroupClick}
        attributeGroupVersion
      >
        <div className={classes.attributeGroupRowListItem}>
          <div
            className={classes.attributeGroupTitle}
            ref={container}
            style={textStyles}
          >
            {!overflowing ? (
              mergedAttributeGroup.name
            ) : (
              <Tooltip
                title={mergedAttributeGroup.name}
                placement="bottom-start"
              >
                <p className={classes.attributeGroupTitle} style={textStyles}>
                  {mergedAttributeGroup.name}
                </p>
              </Tooltip>
            )}
          </div>
          <div className={classes.iconContainer}>
            {mergedAttributeGroup?.expandByDefault && (
              <span className={classes.expandDefaultText}>
                Expanded by default
              </span>
            )}
            <IconButton
              onClick={() => setOpenAttributeCreate(true)}
              size="small"
              tooltip="Add Attribute"
            >
              <Icon icon={ControlPointIcon} />
            </IconButton>
            {showDeleteAttribute && (
              <IconButton
                onClick={() => setOpenAttributeGroupDelete(true)}
                size="small"
                tooltip="Delete"
              >
                <Icon icon={DeleteIcon} />
              </IconButton>
            )}
          </div>
        </div>
      </ListItemWrapper>
      <Droppable
        droppableId={mergedAttributeGroup.id}
        type={DragDropType.ATTRIBUTE}
        isDropDisabled={isDragDropDisabled}
      >
        <List style={styles.list}>
          {mergedAttributeGroup.attributes.map(
            (attribute: SchemaTreeAttribute, index: number) => {
              return (
                <Draggable
                  key={attribute.id}
                  draggableId={attribute.id}
                  index={index}
                  isDragDisabled={isDragDropDisabled}
                >
                  <AttributeRow
                    key={attribute?.id}
                    attribute={attribute}
                    attributeGroupId={mergedAttributeGroup.id}
                    createdAttributeId={createdAttributeId}
                    setCreatedAttributeId={setCreatedAttributeId}
                  />
                </Draggable>
              );
            },
          )}
        </List>
      </Droppable>
      <AddItemModal
        open={openAttributeCreate}
        closeModal={() => setOpenAttributeCreate(false)}
        type="Attribute"
        handleSave={handleAttributeCreate}
        isDisabled={creatingAttribute}
      />
      <EditItemModal
        name={mergedAttributeGroup.name}
        type="Group"
        handleSave={handleAttributeGroupEdit}
        open={openAttributeGroupEdit}
        closeModal={() => setOpenAttributeGroupEdit(false)}
        isDisabled={updatingGroup}
      />
      <DeleteModal
        open={openAttributeGroupDelete}
        onConfirm={handleAttributeGroupDelete}
        onCancel={() => setOpenAttributeGroupDelete(false)}
        title="Delete Group?"
        content={`Deleting ${mergedAttributeGroup.name} will also delete all the items under that group.`}
        cancelText="Cancel"
        confirmText="Delete"
        disabled={deletingGroup}
      />
    </>
  );
};

export default AttributeGroupRow;
